import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Dropdown, Form } from 'react-bootstrap';
import './ProfessionalInformation.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate, useLocation } from 'react-router-dom';
import JobSeekerNavbar from '../../../../Pages/Navbar/JobSeekerNavbar';
import Swal from 'sweetalert2';
import BaseUrl from '../../../../ApiServices/BaseUrl';

const ProfessionalInformation = () => {
  const location = useLocation();
  const { userId } = location.state || {};

  // Log userId to console
  useEffect(() => {
    console.log('Received userId:', userId);
  }, [userId]);

  // State for selected options
  const [medicalSpecialty, setMedicalSpecialty] = useState('');
  const [surgicalSpecialty, setSurgicalSpecialty] = useState('');
  const [otherSpecialty, setOtherSpecialty] = useState('');
  const [academicPosition, setAcademicPosition] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  // Options data
  const medicalSpecialties = [
    'Allergist/Immunologist', 'Anesthesiologist', 'Cardiologist',
    'Dermatologist', 'Endocrinologist', 'Gastroenterologist',
    'General Surgeon', 'Infectious Disease Specialist', 'Nephrologist',
    'Neurologist', 'Oncologist', 'Ophthalmologist',
    'Orthopedic Surgeon', 'Otolaryngologist (ENT)', 'Pathologist',
    'Pediatrician', 'Psychiatrist', 'Pulmonologist',
    'Radiologist', 'Rheumatologist', 'Urologist'
  ];

  const surgicalSpecialties = [
    'Cardiothoracic Surgeon', 'Colorectal Surgeon', 'General Surgeon',
    'Neurosurgeon', 'Oral and Maxillofacial Surgeon', 'Orthopedic Surgeon',
    'Plastic Surgeon', 'Trauma Surgeon', 'Vascular Surgeon'
  ];

  const otherSpecialties = [
    'Emergency Medicine Physician', 'Family Medicine Physician',
    'General Practitioner', 'Intensivist', 'Medical Geneticist',
    'Occupational Medicine Physician', 'Pain Management Specialist',
    'Palliative Care Physician', 'Preventive Medicine Physician',
    'Public Health Physician'
  ];

  const academicPositions = [
    'Professor of Medicine', 'Research Scientist',
    'Clinical Research Coordinator', 'Medical Educator',
    'Dean of Medical School'
  ];

  // Helper function to filter options for dropdown
  const filterOptions = (options, searchTerm) => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Custom Dropdown Component (same as in JobPreference)
  const CustomDropdown = ({
    title,
    field,
    options,
    selectedValue,
    onSelect,
    required = false,
    menuClassName = '',
    disabled = false
  }) => {
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = filterOptions(options, localSearchTerm);

    return (
      <div>
        <Dropdown
          className="mb-3"
          show={isOpen}
          onToggle={(isOpen) => setIsOpen(isOpen)}
          disabled={disabled}
        >
          <Dropdown.Toggle
            variant="light"
            className="language-select w-100 d-flex justify-content-between align-items-center professional-information-select"
            id={`dropdown-${field}`}
            disabled={disabled}
          >
            {selectedValue || `${title}${required ? ' *' : ''}`}
          </Dropdown.Toggle>
          <Dropdown.Menu className={`w-100 ${menuClassName}`}>
            <div className="px-2 py-1">
              <Form.Control
                type="text"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="mb-2"
                autoFocus
              />
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    active={selectedValue === option}
                    onClick={() => {
                      onSelect(field, option);
                      setLocalSearchTerm('');
                      setIsOpen(false);
                    }}
                  >
                    {option}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>No matches found</Dropdown.Item>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  // Fetch existing data
  useEffect(() => {
    const fetchProfessionalData = async () => {
      if (!userId) return;

      try {
        setIsLoading(true);
        const response = await fetch(`${BaseUrl}/api/job-seeker/doctor/${userId}`);

        if (response.ok) {
          const data = await response.json();
          console.log('Fetched professional data:', data);

          // Set professional information fields
          if (data.dtr_medical_job_position && data.dtr_medical_job_position !== "null") {
            setMedicalSpecialty(data.dtr_medical_job_position);
          }

          if (data.dtr_surgical_job_position && data.dtr_surgical_job_position !== "null") {
            setSurgicalSpecialty(data.dtr_surgical_job_position);
          }

          if (data.dtr_other_medical_job_position && data.dtr_other_medical_job_position !== "null") {
            setOtherSpecialty(data.dtr_other_medical_job_position);
          }

          if (data.dtr_academic_job_position && data.dtr_academic_job_position !== "null") {
            setAcademicPosition(data.dtr_academic_job_position);
          }
        } else if (response.status !== 404) {
          throw new Error('Failed to fetch professional data');
        }
      } catch (err) {
        console.error('Error fetching professional data:', err);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load your professional information. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchProfessionalData();
    }
  }, [userId]);

  // Handle dropdown selection
  const handleDropdownSelect = (field, value) => {
    switch (field) {
      case 'medical_specialty':
        setMedicalSpecialty(value);
        break;
      case 'surgical_specialty':
        setSurgicalSpecialty(value);
        break;
      case 'other_specialty':
        setOtherSpecialty(value);
        break;
      case 'academic_position':
        setAcademicPosition(value);
        break;
      default:
        break;
    }
  };

  // Show success alert using SweetAlert2
  const showSuccessAlert = () => {
    Swal.fire({
      title: 'Success!',
      text: 'Professional information saved successfully!',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#3085d6',
    });
  };

  // Handle save data to API
  const handleSave = async () => {
    if (!userId) {
      Swal.fire({
        title: 'Error!',
        text: 'User ID is missing. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    }

    setIsSaving(true);

    try {
      // Prepare the data object for API with only the fields you specified
      const formData = {
        user_id: userId,
        dtr_user_id: userId,
        dtr_medical_job_position: medicalSpecialty,
        dtr_surgical_job_position: surgicalSpecialty,
        dtr_other_medical_job_position: otherSpecialty,
        dtr_academic_job_position: academicPosition
      };

      console.log('Saving professional data:', formData);

      const response = await fetch(`${BaseUrl}/api/job-seeker/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('API Response:', result);
        showSuccessAlert(); // Use SweetAlert instead of regular alert
        return true;
      } else {
        throw new Error('Failed to save professional information');
      }
    } catch (error) {
      console.error('Error saving professional information:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error saving professional information. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle next step
  const handleNext = async () => {
    // Save data first, then navigate
    const success = await handleSave();
    if (success) {
      navigate('/preferencejob', {
        state: { userId: userId }
      });
    }
  };

  // Handle back step
  const handleBack = () => {
    navigate('/jobpreference', {
      state: { userId: userId }
    });
  };

  if (isLoading) {
    return (
      <>
        <JobSeekerNavbar />
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <JobSeekerNavbar />
      <div className="professional-information-wrapper" style={{ marginTop: "84px", width: '96%', marginLeft: '2%' }}>
        <Container className="mt-4 px-4 pb-5">
          <Card className="shadow border-0 professional-info-card">
            <Card.Header className="professional-info-header">
              <h2 className="m-0 text-white text-center">
                <i className="bi bi-person-badge me-2"></i>
                Professional Information
              </h2>
            </Card.Header>
            <Card.Body className="professional-info-body">
              
              {/* Medical Specialties Section */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Select your Medical Specialties Job Position
                </h4>
                
                <CustomDropdown
                  title="Select Medical Specialty"
                  field="medical_specialty"
                  options={medicalSpecialties}
                  selectedValue={medicalSpecialty}
                  onSelect={handleDropdownSelect}
                  required={false}
                  menuClassName="personalinfo-nationality-menu"
                />
              </div>

              {/* Surgical Specialties Section */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Select your Surgical Specialties Job Position
                </h4>
                
                <CustomDropdown
                  title="Select Surgical Specialty"
                  field="surgical_specialty"
                  options={surgicalSpecialties}
                  selectedValue={surgicalSpecialty}
                  onSelect={handleDropdownSelect}
                  required={false}
                  menuClassName="personalinfo-nationality-menu"
                />
              </div>

              {/* Other Specialties Section */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Select Other Medical Specialties Job Position
                </h4>
                
                <CustomDropdown
                  title="Select Other Specialty"
                  field="other_specialty"
                  options={otherSpecialties}
                  selectedValue={otherSpecialty}
                  onSelect={handleDropdownSelect}
                  required={false}
                  menuClassName="personalinfo-nationality-menu"
                />
              </div>

              {/* Academic Positions Section */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Select Academic and Research Job Position
                </h4>
                
                <CustomDropdown
                  title="Select Academic Position"
                  field="academic_position"
                  options={academicPositions}
                  selectedValue={academicPosition}
                  onSelect={handleDropdownSelect}
                  required={false}
                  menuClassName="personalinfo-nationality-menu"
                />
              </div>

              {/* Navigation Buttons */}
              <div className="additional-section mb-4" style={{ marginTop: '20px' }}>
                <div className="d-flex justify-content-between gap-2" style={{ width: "100%" }}>
                  <button
                    className="nav-btn"
                    style={{ flex: 1 }}
                    onClick={handleBack}
                    disabled={isSaving}
                  >
                    Back
                  </button>

                  <button
                    className="nav-btn"
                    style={{ flex: 1 }}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>

                  <button
                    className="nav-btn"
                    style={{ flex: 1 }}
                    onClick={handleNext}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Next'}
                  </button>
                </div>
              </div>

            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default ProfessionalInformation;